import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { scrollToTop } from '../utilities/general-utils'
import { FFText, FFDate, FFToggle, FFSelect, FFDayGridSelector, RCFormProvider, useRCFormContext } from '@jlafica/rcform';
import { redirectAfterTimeout, getRecurrenceDisplayString } from '../utilities/general-utils'
import { svc_getAccounts } from "../services/account.service";
import { svc_createTransaction } from "../services/transaction.service";
import {
    fieldNames,
    initialState,
    getTransactionSubmissionModel,
    getTransactionRawModelFromFieldValues,
    getTransactionFormFieldsModel,
    dayOfWeekFieldOptions,
} from '../models/TransactionModel'
import { useResponseMessages } from "../hooks/useResponseMessage";

const TransactionCreate = ({
    transactionItem,
    onCreate,
    isSnowball = false,
    redirectOnCreate = true,
    autoCreate = false,
    onTransactionChange = null
}) => {
    const { getAccessTokenSilently, user } = useAuth0();
    const navigate = useNavigate();
    const [formCtx, setFormCtx] = useState(null);
    const initialCreateState = { ...initialState }
    const [accounts, setAccounts] = useState(null)
    const [recurrenceDisplayString, setRecurrenceDisplayString] = useState('')
    const [dayOfWeekOptions, setDayOfWeekOptions] = useState([
        ...dayOfWeekFieldOptions.weekly
    ]);
    const { addMessage } = useResponseMessages();

    const transaction = transactionItem ? {
            ...transactionItem,
            startDate: transactionItem.startDate,
            endDate: transactionItem.endDate
                ? transactionItem.endDate
                : null,
            isSnowball: isSnowball ? 1 : 0
        } : {
            title: '',
            description: '',
            amount: null,
            category: '',
            subcategory: '',
            account: null,
            startDate: '',
            endDate: '',
            isRecurring: 0,
            recurringType: null,
            dayOfWeek: null,
            dayOfMonthList: [],
            weekOfMonth: null,
            monthOfYear: null,
            separationCount: 1,
            isSnowball: isSnowball ? 1 : 0
        }


    const getAccounts = async () => {
        const accessToken = await getAccessTokenSilently()
        const { data } = await svc_getAccounts(accessToken, user.sub);

        if (data) {
            let accountOptions = data.map(account => {
                return {
                    value: account.id,
                    label: account.name
                }
            })
            setAccounts(accountOptions)
        }
    }

    const displayRecurrenceMessage = (formState) => {
        const transactionEditObject = getTransactionRawModelFromFieldValues(null, formState);
        setRecurrenceDisplayString(getRecurrenceDisplayString(transactionEditObject))
    }

    const handleSubmit = async (e) => {
        if (e) e.preventDefault()

        const submissionObject = getTransactionSubmissionModel(null, formCtx?.formState);
        const currentFormState = formCtx?.validateForm(formCtx?.formState)

        if (!currentFormState.isValid) {
            addMessage({
                error: 'Please correct the errors in the form'
            })

            return
        }

        const accessToken = await getAccessTokenSilently()
        const { data, error } = await svc_createTransaction(accessToken, user.sub, submissionObject)

        if (error) {
            console.error("Error adding transaction: ", error)
            addMessage({
                error: 'Error adding transaction'
            })
            scrollToTop()
            return
        }

        if (onCreate) {
            onCreate(accessToken, { data, error })
        }
        
        if (redirectOnCreate) {
            addMessage({
                data: 'Transaction successfully added'
            })
            redirectAfterTimeout(`/transaction/${data.insertId}`, 2_000, navigate)
            scrollToTop()
            return
        }
        
        addMessage({
            data: 'Transaction successfully added'
        })
    }

    useEffect(() => {
        if (!accounts) {
            getAccounts()
        }
        if (autoCreate && accounts && transaction) {
            handleSubmit()
        }
    }, [accounts, autoCreate])

    useEffect(() => {
        if (transaction && formCtx?.formState && !formCtx?.formState.hasAppliedInitialValues) {
            const transObj = getTransactionFormFieldsModel(transaction);
            formCtx?.updateFieldValues(transObj)
        }
    }, [formCtx?.formState?.hasAppliedInitialValues])

    useEffect(() => {
        if(formCtx?.formState.fields[fieldNames.isRecurring].checked) {
            displayRecurrenceMessage(formCtx?.formState)
        }
    }, [
        formCtx?.formState.fields[fieldNames.isRecurring].checked,
        formCtx?.formState.fields[fieldNames.recurringType].value,
        formCtx?.formState.fields[fieldNames.dayOfWeek].value,
        formCtx?.formState.fields[fieldNames.dayOfMonthList].selectedValues,
        formCtx?.formState.fields[fieldNames.weekOfMonth].value,
        formCtx?.formState.fields[fieldNames.monthOfYear].value,
        formCtx?.formState.fields[fieldNames.dateFrequencyType].value,
        formCtx?.formState.fields[fieldNames.separationCount].value
    ])

    return (
        <>
            <h1 className="text-center">Add Transaction</h1>

            <div className="standardFormWrap">
                <RCFormProvider
                    onSubmit={handleSubmit}
                    id="uploadTransactionForm"
                    className={`formStandard ${!formCtx?.formState?.isValid ? 'errors' : ''}`}
                    initialFieldState={initialCreateState}
                    setFormRef={setFormCtx}
                >
                    <FormContent
                        accounts={accounts}
                        recurrenceDisplayString={recurrenceDisplayString}
                        fieldNames={fieldNames}
                        dayOfWeekOptions={dayOfWeekOptions}
                        setDayOfWeekOptions={setDayOfWeekOptions}
                    />
                </RCFormProvider>
            </div>
        </>
    )
}

const FormContent = ({
    accounts,
    recurrenceDisplayString,
    fieldNames,
    dayOfWeekOptions,
    setDayOfWeekOptions,
}) => {
    const formContext = useRCFormContext()

    useEffect(() => {
        if (formContext.formState.fields[fieldNames.isRecurring].checked) {
            if (formContext.formState.fields[fieldNames.recurringType].value === 'monthly') {
                setDayOfWeekOptions([...dayOfWeekFieldOptions.monthly])
            } else if (formContext.formState.fields[fieldNames.recurringType].value === 'weekly') {
                setDayOfWeekOptions([...dayOfWeekFieldOptions.weekly])
            }
        }
    }, [
        formContext.formState.fields[fieldNames.isRecurring].checked,
        formContext.formState.fields[fieldNames.recurringType].value
    ])

    // Reset conditional weekly and monthly fields if date frequency type changes
    const handleDateFrequencyTypeChange = (e) => {
        let fieldsToReset = {
            [fieldNames.dayOfMonthList]: { selectedValues: [] },
            [fieldNames.dayOfWeek]: { value: null },
            [fieldNames.weekOfMonth]: { value: null }
        }

        if (e.target.name === fieldNames.dateFrequencyType) {
            fieldsToReset[fieldNames.dateFrequencyType] = { value: e.target.value }
        }

        if (e.target.name === fieldNames.recurringType) {
            fieldsToReset[fieldNames.recurringType] = { value: e.target.value }
        }

        formContext.updateFieldValues(fieldsToReset)
    }

    return (
        <>
            <FFText
                fieldName={fieldNames.title}
                label="Title*"
                wrapperModifierClass={"formField"}
                required={true}
            />

            <FFText
                fieldName={fieldNames.description}
                label="Description"
                wrapperModifierClass={"formField"}
            />

            <FFText
                type="number"
                label="Amount*"
                fieldName={fieldNames.amount}
                wrapperModifierClass={"formField"}
                required={true}
                formatValue={(val) => {return val !== '' ? parseFloat(val) : val}}
                additionalProps={{
                    step: .01
                }}
            />

            <FFText
                label="Category*"
                fieldName={fieldNames.category}
                wrapperModifierClass={"formField"}
                required={true}
            />

            <FFText
                label="Subcategory"
                fieldName={fieldNames.subcategory}
                wrapperModifierClass={"formField"}
            />

            <FFSelect
                label="Debit Account*"
                fieldName={fieldNames.account}
                options={[
                    {value: '', label: ''},
                    ...(accounts ? accounts : [])
                ]}
                required={true}
                wrapperModifierClass={"formField"}
            />

            <FFDate
                label="Start Date*"
                fieldName={fieldNames.startDate}
                required={true}
                wrapperModifierClass={"formField"}
            />

            <FFDate
                label="End Date"
                fieldName={fieldNames.endDate}
                wrapperModifierClass={"formField"}
                pattern={null}
            />

            <FFToggle
                label="Recurrence"
                buttonOnValue="Recurring"
                buttonOffValue="One-Time"
                fieldName={fieldNames.isRecurring}
                wrapperModifierClass="formField"
            />

            {/* Recurring Fields */}

            <div style={{
                display: formContext.formState.fields[fieldNames.isRecurring].checked ? 'block' : 'none'
            }}>
                <FFSelect
                    label="Frequency*"
                    fieldName={fieldNames.recurringType}
                    options={[
                        { value: '', label: 'Select a frequency option' },
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                    wrapperModifierClass={`formField`}
                    required={formContext.formState.fields[fieldNames.isRecurring].checked}
                    customErrorMessage={"This field is required for recurring transactions"}
                    onChange={handleDateFrequencyTypeChange}
                />

                <FFSelect
                    label="Month of Year*"
                    fieldName={fieldNames.monthOfYear}
                    formatValue={parseInt}
                    options={[
                        { value: '', label: 'Select a Month' },
                        { value: 0, label: 'January' },
                        { value: 1, label: 'February' },
                        { value: 2, label: 'March' },
                        { value: 3, label: 'April' },
                        { value: 4, label: 'May' },
                        { value: 5, label: 'June' },
                        { value: 6, label: 'July' },
                        { value: 7, label: 'August' },
                        { value: 8, label: 'September' },
                        { value: 9, label: 'October' },
                        { value: 10, label: 'November' },
                        { value: 11, label: 'December' },
                    ]}
                    displayCondition={formContext.formState.fields[fieldNames.recurringType].value === 'yearly'}
                    required={formContext.formState.fields[fieldNames.recurringType].value === 'yearly'}
                    wrapperModifierClass={`formField`}
                />

                <FFSelect
                    label="Date Frequency*"
                    fieldName={fieldNames.dateFrequencyType}
                    options={[
                        { value: '', label: '' },
                        { value: 1, label: 'Specific date(s)' },
                        { value: 2, label: 'Nth occurrence' },
                    ]}
                    displayCondition={
                        formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                        formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                    }
                    required={
                        formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                        formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                    }
                    wrapperModifierClass={`formField`}
                    onChange={handleDateFrequencyTypeChange}
                />

                <div
                    className={
                        (
                            formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                            formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                        ) &&
                        parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            ? 'formField-row'
                            : ''
                    }
                >

                    {/* weekOfMonth */}

                    <FFSelect
                        label="Nth day*"
                        fieldName={fieldNames.weekOfMonth}
                        options={[
                            { value: '',       label: ''     },
                            { value: '1',      label: '1st'  },
                            { value: '2',      label: '2nd'  },
                            { value: '3',      label: '3rd'  },
                            { value: '4',      label: '4th'  },
                            { value: '5',      label: '5th'  },
                            { value: 'last',   label: 'Last' }
                        ]}
                        wrapperModifierClass={`formField`}
                        displayCondition={
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                            ) &&
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                        }
                        required={
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                            ) &&
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                        }
                    />

                    {/* dayOfWeek */}

                    <FFSelect
                        label="Day of Week*"
                        fieldName={fieldNames.dayOfWeek}
                        formatValue={parseInt}
                        options={dayOfWeekOptions}
                        displayCondition={(
                            formContext.formState.fields[fieldNames.recurringType].value === 'weekly' ||
                            (
                                (
                                    formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                    formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                                ) &&
                                parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            )
                        )}
                        required={(
                            formContext.formState.fields[fieldNames.recurringType].value === 'weekly' ||
                            (
                                (
                                    formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                    formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                                ) &&
                                parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            )
                        )}
                        wrapperModifierClass={`formField`}
                    />
                </div>

                <div>
                    <FFDayGridSelector
                        label="Day(s) of Month*"
                        fieldName={fieldNames.dayOfMonthList}
                        displayCondition={
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 1 &&
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly'
                            )
                        }
                        required={
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 1 &&
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly'
                            )
                        }
                        wrapperModifierClass={`formField`}
                    />
                </div>

                <FFText
                    type="number"
                    label="Occurrence Interval*"
                    fieldName={fieldNames.separationCount}
                    formatValue={(val) => {return val !== '' ? parseInt(val) : val}}
                    additionalProps={{
                        min: 1
                    }}
                    wrapperModifierClass={`formField`}
                    required={formContext.formState.fields[fieldNames.isRecurring].checked}
                />
            </div>

            {formContext.formState.fields[fieldNames.isRecurring].checked && (
                <div>
                    <h5 className="spc-m-t-1 spc-m-b-half">Frequency Result:</h5>
                    <p>{recurrenceDisplayString}</p>
                </div>
            )}

            <div className="formField">
                <button type="submit" className="button button-green">
                    Submit Transaction
                </button>
            </div>
        </>
    )
}

export default TransactionCreate
